/**
 * Auth utilities — standalone replacement for shared auth helpers.
 * Reads session token and roles from localStorage/sessionStorage where the
 * host application (c2b-portal / Bifrost) writes them.
 */
const LOCAL_STORAGE_KEYS = {
  SESSION_TOKEN: 'DS',
  SESSION_TOKEN_ALT: 'accessToken',
  USER_ROLES: 'appRoles',
  USER_ROLES_ALT: 'userRoles',
  USER_EMAIL: 'userEmail',
  USER_PERMISSIONS: 'user-permissions',
} as const

const SESSION_STORAGE_KEYS = {
  USER_ROLES: 'userRoles',
  USER_PERMISSIONS: 'user-permissions',
} as const

const parseRoleList = (raw: string | null | undefined): string[] => {
  if (!raw?.trim()) return []

  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((role): role is string => typeof role === 'string') : []
  } catch {
    return raw.split(',').map((role) => role.trim()).filter(Boolean)
  }
}

export const getSessionToken = (): string | null => {
  return (
    localStorage.getItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN_ALT) ||
    localStorage.getItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN) ||
    null
  )
}

export const getRoles = (): string[] => {
  try {
    const raw =
      sessionStorage.getItem(SESSION_STORAGE_KEYS.USER_ROLES) ||
      localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ROLES) ||
      localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ROLES_ALT)
    return parseRoleList(raw)
  } catch {
    return []
  }
}

export const getUserRoles = (): string[] => getRoles()

export const getLoginUserEmail = (): string => {
  return localStorage.getItem(LOCAL_STORAGE_KEYS.USER_EMAIL) ?? ''
}

export const getLoginUserMobile = (): string => {
  try {
    const raw = localStorage.getItem('user-info')
    if (!raw) return ''
    const value = JSON.parse(raw) as { mobile?: unknown }
    return typeof value.mobile === 'string' ? value.mobile : ''
  } catch {
    return ''
  }
}

export const getLoginUserId = (): string => {
  try {
    const raw = localStorage.getItem('user-info')
    if (!raw) return ''
    const value = JSON.parse(raw) as { id?: unknown; _id?: unknown }
    const id = value.id ?? value._id
    return typeof id === 'string' ? id : ''
  } catch {
    return ''
  }
}

export const getUserPermissions = (): string[] => {
  try {
    const raw =
      sessionStorage.getItem(SESSION_STORAGE_KEYS.USER_PERMISSIONS) ||
      localStorage.getItem(LOCAL_STORAGE_KEYS.USER_PERMISSIONS)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/** Remove authentication state without wiping user preferences and UI drafts. */
export const clearAuthStorage = () => {
  ;[
    'Auth-Type', 'DS', 'accessToken', 'token', 'appRoles', 'userRoles', 'user-roles',
    'userEmail', 'user-permissions', 'refreshToken', 'sessionId', 'user-info',
  ].forEach((key) => {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
  })
}

export const handleUnauthorized = () => {
  clearAuthStorage()
  window.location.href = `${window.location.origin}/login`
}

/** Store auth data returned by the login API and reload into the app. */
export const persistAuthAndRedirect = (data: {
  accessToken: string
  userRoles?: string[]
  userEmail?: string
  userName?: string
  refreshToken?: string
  sessionId?: string
  userInfo?: Record<string, unknown>
  permissions?: string[]
  user?: { id?: string; email?: string; name?: string; roles?: string[] }
}) => {
  const roles = data.user?.roles ?? data.userRoles ?? []

  localStorage.setItem('Auth-Type', 'JWT')
  localStorage.setItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN_ALT, data.accessToken)
  localStorage.setItem('token', data.accessToken)
  if (roles.length) {
    localStorage.setItem(LOCAL_STORAGE_KEYS.USER_ROLES, JSON.stringify(roles))
    localStorage.setItem(LOCAL_STORAGE_KEYS.USER_ROLES_ALT, JSON.stringify(roles))
    localStorage.setItem('user-roles', JSON.stringify(roles))
  }
  const email = data.user?.email ?? data.userEmail
  if (email) {
    localStorage.setItem(LOCAL_STORAGE_KEYS.USER_EMAIL, email)
  } else {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_EMAIL)
  }
  if (data.refreshToken) {
    localStorage.setItem('refreshToken', data.refreshToken)
  }
  if (data.sessionId) {
    localStorage.setItem('sessionId', data.sessionId)
  }
  if (data.userInfo) {
    localStorage.setItem('user-info', JSON.stringify(data.userInfo))
  }
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.USER_PERMISSIONS,
    JSON.stringify(data.permissions ?? []),
  )
  // Navigate into the app — replace so back button doesn't return to /login
  window.location.replace(`${window.location.origin}/`)
}

/** Check if the current user has at least one of the given roles. */
export const hasAnyRole = (requiredRoles: string[]): boolean => {
  const userRoles = getRoles()
  const required = new Set(requiredRoles)
  return userRoles.some((r) => required.has(r))
}
